import {
  type BooleanProviderOptionDescriptor,
  FEATURE_FLAG_CAPABILITIES,
  type FeatureFlagAdminState,
  type FeatureFlagCapabilityKey,
  type FeatureFlagIssue,
  type FeatureFlagKey,
  type FeatureFlagOrigin,
  type FeatureFlagPatch,
  type ModelSelection,
  type ProviderOptionSelection,
  type SelectProviderOptionDescriptor,
  type ServerProviderModel,
} from "@t3tools/contracts";

const REASONING_EFFORT_OPTION_ID = "reasoningEffort";
const FAST_MODE_OPTION_ID = "fastMode";

export interface CapabilityRow {
  readonly key: FeatureFlagCapabilityKey;
  readonly fileKey: string;
  readonly enabled: boolean;
  readonly origin: FeatureFlagOrigin;
}

export function buildCapabilityRows(state: FeatureFlagAdminState): ReadonlyArray<CapabilityRow> {
  return FEATURE_FLAG_CAPABILITIES.map((definition) => ({
    key: definition.key,
    fileKey: definition.fileKey,
    enabled: state.values[definition.key],
    origin: state.origins[definition.key],
  }));
}

export function capabilityPatch(key: FeatureFlagCapabilityKey, enabled: boolean): FeatureFlagPatch {
  return Object.fromEntries([[key, enabled]]) as FeatureFlagPatch;
}

export function resetPatch(key: FeatureFlagKey): FeatureFlagPatch {
  return Object.fromEntries([[key, null]]) as FeatureFlagPatch;
}

export function issuesFor(
  issues: ReadonlyArray<FeatureFlagIssue>,
  key: FeatureFlagKey,
): ReadonlyArray<FeatureFlagIssue> {
  return issues.filter((issue) => issue.kind === "invalid-value" && issue.key === key);
}

export function fileIssues(
  issues: ReadonlyArray<FeatureFlagIssue>,
): ReadonlyArray<FeatureFlagIssue> {
  return issues.filter((issue) => issue.kind === "invalid-file");
}

export function findModel(
  models: ReadonlyArray<ServerProviderModel>,
  slug: string,
): ServerProviderModel | undefined {
  return models.find((model) => model.slug === slug);
}

export function reasoningEffortDescriptor(
  model: ServerProviderModel | undefined,
): SelectProviderOptionDescriptor | undefined {
  return model?.capabilities?.optionDescriptors?.find(
    (descriptor): descriptor is SelectProviderOptionDescriptor =>
      descriptor.type === "select" && descriptor.id === REASONING_EFFORT_OPTION_ID,
  );
}

export function fastModeDescriptor(
  model: ServerProviderModel | undefined,
): BooleanProviderOptionDescriptor | undefined {
  return model?.capabilities?.optionDescriptors?.find(
    (descriptor): descriptor is BooleanProviderOptionDescriptor =>
      descriptor.type === "boolean" && descriptor.id === FAST_MODE_OPTION_ID,
  );
}

function optionValue(selection: ModelSelection, id: string): string | boolean | undefined {
  return selection.options?.find((option) => option.id === id)?.value;
}

function defaultReasoningEffort(descriptor: SelectProviderOptionDescriptor): string | undefined {
  return (
    descriptor.options.find((option) => option.isDefault)?.id ??
    descriptor.currentValue ??
    descriptor.options[0]?.id
  );
}

export function selectedReasoningEffort(
  selection: ModelSelection,
  model: ServerProviderModel | undefined,
): string | undefined {
  const descriptor = reasoningEffortDescriptor(model);
  if (!descriptor) return undefined;
  const value = optionValue(selection, REASONING_EFFORT_OPTION_ID);
  return typeof value === "string" ? value : defaultReasoningEffort(descriptor);
}

export function selectedFastMode(selection: ModelSelection): boolean {
  return optionValue(selection, FAST_MODE_OPTION_ID) === true;
}

// Every option the model offers is written explicitly: an option id the value
// leaves unset would pass through from the client's own selection.
function modelValue(
  selection: ModelSelection,
  model: ServerProviderModel,
  choices: { readonly reasoningEffort: string | undefined; readonly fastMode: boolean },
): ModelSelection {
  const options: Array<ProviderOptionSelection> = [];
  if (reasoningEffortDescriptor(model) && choices.reasoningEffort !== undefined) {
    options.push({ id: REASONING_EFFORT_OPTION_ID, value: choices.reasoningEffort });
  }
  if (fastModeDescriptor(model)) {
    options.push({ id: FAST_MODE_OPTION_ID, value: choices.fastMode });
  }
  return {
    instanceId: selection.instanceId,
    model: model.slug,
    ...(options.length > 0 ? { options } : {}),
  };
}

export function withModel(selection: ModelSelection, model: ServerProviderModel): ModelSelection {
  const descriptor = reasoningEffortDescriptor(model);
  return modelValue(selection, model, {
    reasoningEffort: descriptor ? defaultReasoningEffort(descriptor) : undefined,
    fastMode: selectedFastMode(selection),
  });
}

export function withReasoningEffort(
  selection: ModelSelection,
  model: ServerProviderModel,
  reasoningEffort: string,
): ModelSelection {
  return modelValue(selection, model, {
    reasoningEffort,
    fastMode: selectedFastMode(selection),
  });
}

export function withFastMode(
  selection: ModelSelection,
  model: ServerProviderModel,
  fastMode: boolean,
): ModelSelection {
  return modelValue(selection, model, {
    reasoningEffort: selectedReasoningEffort(selection, model),
    fastMode,
  });
}
